/**
 * Dexie database for web — schema preserved from legacy `frontend/src/db/localDb.ts`.
 *
 * CP2: this is the ONLY module in the repo allowed to import `dexie`. Every
 * query goes through `DexieStorage` (see `storage.ts`), which conforms to
 * `IStorage`.
 *
 * Schema evolution (v1 → v15) is copied verbatim so an existing user's
 * IndexedDB continues to work unchanged. v7 (T12.0.1.2, 2026-05-04) drops the
 * legacy `templates` store — the templates feature was deleted end-to-end on
 * the backend; remaining client-side cached template rows are removed. v8
 * (T12.0.1.3, 2026-05-04) renames the FK on cached MyProducts:
 * `seed_product_id` → `seed_myproduct_id`. Backward-incompatible wire-format
 * change — every household with synced MyProducts holds the legacy field, so
 * the upgrade callback walks `cachedProducts` and rewrites the key. v10
 * (T12.0.18.1, 2026-05-26) cascades the D7 table-rename sweep into cached
 * row payloads (my_product_id → householdproduct_id, shopping_list_id →
 * shoppinglist_id, etc.). v11 (T12.0.18.1.1, 2026-05-27) cascaded an
 * experimental ADR-003 D2 within-chunk column collapse into cached payloads
 * (display_name → displayname, etc.). v12 (T12.0.18.1.1 follow-up,
 * 2026-05-27) reverts v11's payload-key collapse — wire shape and storage
 * shape align on snake_case-with-new-term again; indexed-column declarations
 * and upgrade callback mirror v11 in reverse so v9/v10 clients that already
 * upgraded through v11 land on the snake_case end state without losing data.
 * v15 (T12.0.18.5, 2026-05-28) drops the legacy `purchaseStats` +
 * `cachedSuggestions` stores and creates fresh `purchaseCadences` +
 * `productSuggestions` stores on a surrogate UUID `id` pkey with indexed
 * `householdproduct_id`. The drop + recreate carries no `.upgrade()` callback;
 * cached rows on existing dev installs are dropped and re-populated from the
 * server on next sync.
 */

import Dexie, { type Table } from 'dexie';
import type {
  HouseholdProduct,
  ShoppingList,
  ShoppingListItem,
} from '@shopista/shared/types';
import type {
  PendingEvent,
  ProductSuggestion,
  PurchaseCadence,
  StoreLocation_RetailerOffer_AislePosition,
  SyncState,
} from '../interface/index.js';

/**
 * Legacy row shapes (v1–v2 tables). These exist to let the Dexie schema be
 * ported byte-identically; they are not part of the IStorage surface today.
 * Component code that still reads these tables during the migration can reach
 * into `database.items` / `database.stores` directly. The `templates` store
 * was dropped in v7 (T12.0.1.2).
 */
export interface LegacyItem {
  id: string;
  household_id: string;
  name: string;
  is_needed: boolean;
  status: string;
  [k: string]: unknown;
}

export interface LegacyStore {
  id: string;
  household_id: string;
  name: string;
  [k: string]: unknown;
}

export interface LegacyPendingEvent {
  id: string;
  itemId: string;
  actionType: string;
  clientTimestamp: string;
  userId: string;
  synced: boolean;
  [k: string]: unknown;
}

export const SHOPPING_DB_NAME = 'ShoppingCoordinationDB';
export const SHOPPING_DB_VERSION = 15;

/**
 * Dexie subclass mirroring the legacy schema. Kept identical to
 * `frontend/src/db/localDb.ts` so existing IndexedDB data opens without
 * migration.
 */
export class ShoppingDatabase extends Dexie {
  // Legacy tables (v1-v2). `templates` was dropped in v7 (T12.0.1.2).
  items!: Table<LegacyItem, string>;
  stores!: Table<LegacyStore, string>;
  pendingEvents!: Table<LegacyPendingEvent, string>;
  syncState!: Table<SyncState, string>;

  // New architecture tables (v3+ — renamed with `cached` prefix in v5)
  cachedListItems!: Table<ShoppingListItem, string>;
  cachedProducts!: Table<HouseholdProduct, string>;
  shoppingLists!: Table<ShoppingList, string>;
  pendingEventsV2!: Table<PendingEvent, string>;
  syncStateV2!: Table<SyncState, string>;

  // Purchase history + suggestions. Original stores `purchaseStats` +
  // `cachedSuggestions` created in v4; v15 (T12.0.18.5) dropped both and
  // recreated them under their entity-aligned names with a surrogate UUID
  // `id` pkey.
  purchaseCadences!: Table<PurchaseCadence, string>;
  productSuggestions!: Table<ProductSuggestion, string>;

  // Per-StoreLocation aisle position (v6 as storeItemOrder; restructured in
  // v13 — T12.0.18.2 — to a shared (storelocation, retaileroffer) junction).
  aislePositions!: Table<StoreLocation_RetailerOffer_AislePosition, string>;

  constructor(name: string = SHOPPING_DB_NAME) {
    super(name);

    // v1 — initial schema
    this.version(1).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',
    });

    // v2 — templates
    this.version(2).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      templates: 'id, household_id, is_personal, name, usage_count, category',
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',
    });

    // v3 — new architecture (ShoppingListItems, MyProducts, ShoppingLists)
    this.version(3).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      templates: 'id, household_id, is_personal, name, usage_count, category',
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',

      shoppingListItems: 'id, shopping_list_id, my_product_id, checked, status',
      myProducts: 'id, household_id, display_name, category_id',
      shoppingLists: 'id, household_id, status, list_type',
      pendingEventsV2:
        'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
      syncStateV2: 'deviceId',
    });

    // v4 — purchase history + suggestions
    this.version(4).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      templates: 'id, household_id, is_personal, name, usage_count, category',
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',

      shoppingListItems: 'id, shopping_list_id, my_product_id, checked, status',
      myProducts: 'id, household_id, display_name, category_id',
      shoppingLists: 'id, household_id, status, list_type',
      pendingEventsV2:
        'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
      syncStateV2: 'deviceId',

      purchaseStats: 'my_product_id, household_id, last_purchased_at',
      cachedSuggestions: 'my_product_id, household_id, score',
    });

    // v5 — rename tables (cached* prefix), drop old names
    this.version(5).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      templates: 'id, household_id, is_personal, name, usage_count, category',
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',

      cachedListItems: 'id, shopping_list_id, my_product_id, checked, status',
      cachedProducts: 'id, household_id, display_name, category_id',
      shoppingLists: 'id, household_id, status, list_type',
      pendingEventsV2:
        'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
      syncStateV2: 'deviceId',

      // Deleted old table names
      shoppingListItems: null,
      myProducts: null,

      purchaseStats: 'my_product_id, household_id, last_purchased_at',
      cachedSuggestions: 'my_product_id, household_id, score',
    });

    // v6 — per-store item ordering
    this.version(6).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      templates: 'id, household_id, is_personal, name, usage_count, category',
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',

      cachedListItems: 'id, shopping_list_id, my_product_id, checked, status',
      cachedProducts: 'id, household_id, display_name, category_id',
      shoppingLists: 'id, household_id, status, list_type',
      pendingEventsV2:
        'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
      syncStateV2: 'deviceId',

      purchaseStats: 'my_product_id, household_id, last_purchased_at',
      cachedSuggestions: 'my_product_id, household_id, score',

      storeItemOrder: 'id, store_id, my_product_id, [store_id+my_product_id]',
    });

    // v7 — drop legacy `templates` store (T12.0.1.2, 2026-05-04). The
    // templates feature was removed end-to-end on the backend; any locally
    // cached template rows are dropped on upgrade. Mirrors the v5 pattern that
    // dropped `shoppingListItems` / `myProducts` via `: null`.
    this.version(7).stores({
      items: 'id, household_id, name, is_needed, assigned_store_id, status',
      stores: 'id, household_id, name',
      templates: null,
      pendingEvents: 'id, itemId, synced, clientTimestamp',
      syncState: 'deviceId',

      cachedListItems: 'id, shopping_list_id, my_product_id, checked, status',
      cachedProducts: 'id, household_id, display_name, category_id',
      shoppingLists: 'id, household_id, status, list_type',
      pendingEventsV2:
        'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
      syncStateV2: 'deviceId',

      purchaseStats: 'my_product_id, household_id, last_purchased_at',
      cachedSuggestions: 'my_product_id, household_id, score',

      storeItemOrder: 'id, store_id, my_product_id, [store_id+my_product_id]',
    });

    // v8 — rename `cachedProducts.seed_product_id` → `seed_myproduct_id`
    // (T12.0.1.3, 2026-05-04). Indexes don't include this field, so the
    // schema declaration is unchanged; the upgrade callback rewrites the
    // FK key on every cached MyProduct row. Synonyms aren't stored
    // client-side (server-only lookups) so no other table needs walking.
    this.version(8)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems: 'id, shopping_list_id, my_product_id, checked, status',
        cachedProducts: 'id, household_id, display_name, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        purchaseStats: 'my_product_id, household_id, last_purchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        storeItemOrder: 'id, store_id, my_product_id, [store_id+my_product_id]',
      })
      .upgrade(async (tx) => {
        await tx
          .table('cachedProducts')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('seed_product_id' in row) {
              row.seed_myproduct_id = row.seed_product_id;
              delete row.seed_product_id;
            }
            // Drop the legacy `platform_catalog_product_id` alias if present
            // — the shared schema accepted both names pre-T12.0.1.3 and the
            // legacy alias was removed in the same Task.
            if ('platform_catalog_product_id' in row) {
              if (row.seed_myproduct_id == null) {
                row.seed_myproduct_id = row.platform_catalog_product_id;
              }
              delete row.platform_catalog_product_id;
            }
          });
      });

    // v9 — drop `cachedProducts.preferred_retailer_ids` / `fallback_retailer_ids`
    // (T12.0.1.4, 2026-05-04). The two array fields are replaced server-side by
    // the `my_product_retailer_preferences` junction table (CASCADE on both
    // sides). Locally, the same data lives in `MyProduct.retailer_preferences`
    // (an array of `{ my_product_id, retailer_id, created_at }`). The upgrade
    // callback merges legacy preferred + fallback arrays into junction-row
    // shape and drops both legacy keys. No new index — preferences are read
    // by walking the array on the cached MyProduct row.
    this.version(9)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems: 'id, shopping_list_id, my_product_id, checked, status',
        cachedProducts: 'id, household_id, display_name, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [myProductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        purchaseStats: 'my_product_id, household_id, last_purchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        storeItemOrder: 'id, store_id, my_product_id, [store_id+my_product_id]',
      })
      .upgrade(async (tx) => {
        await tx
          .table('cachedProducts')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            const productId =
              typeof row.id === 'string' ? row.id : '';
            const created =
              typeof row.created_at === 'string'
                ? row.created_at
                : new Date(0).toISOString();
            const preferred = Array.isArray(row.preferred_retailer_ids)
              ? (row.preferred_retailer_ids as string[])
              : [];
            const fallback = Array.isArray(row.fallback_retailer_ids)
              ? (row.fallback_retailer_ids as string[])
              : [];
            const dedup = new Set<string>([...preferred, ...fallback]);
            const existingPrefs = Array.isArray(row.retailer_preferences)
              ? (row.retailer_preferences as Array<{ retailer_id?: string }>)
              : [];
            for (const p of existingPrefs) {
              if (typeof p?.retailer_id === 'string') dedup.add(p.retailer_id);
            }
            row.retailer_preferences = Array.from(dedup).map((retailer_id) => ({
              my_product_id: productId,
              retailer_id,
              created_at: created,
            }));
            delete row.preferred_retailer_ids;
            delete row.fallback_retailer_ids;
          });
      });

    // v10 — T12.0.18.1 D7 + cascade rename. Postgres-side table renames
    // (my_products -> householdproducts, store_offers -> retaileroffers,
    // store_locations -> storelocations, shopping_lists -> shoppinglists, etc.)
    // cascade to cached row payloads. Indexed columns rebuild with the new key
    // name; non-indexed payload keys (including nested retailer_preferences[]
    // and the cachedProducts.seed_myproduct_id payload key) are rewritten by
    // the upgrade callback.
    //
    // Caveats:
    // - purchaseStats and cachedSuggestions: their Postgres FK column
    //   `my_product_id` becomes `householdproduct_id`, but the Dexie primary
    //   key cannot be changed (Dexie limitation: "Not yet support for changing
    //   primary key"). The cached tables keep `my_product_id` as the Dexie pkey
    //   for v10; the sync layer maps the renamed Postgres column to the legacy
    //   Dexie pkey name. Resolved at v15 (T12.0.18.5): both stores were
    //   dropped and recreated under their entity-aligned names
    //   (`purchaseCadences`, `productSuggestions`) on a surrogate UUID `id`
    //   pkey with indexed `householdproduct_id`.
    // - storeItemOrder: owned by T12.0.18.2 — its store_id + my_product_id
    //   index keys stay until that Task lands the restructure.
    this.version(10)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems:
          'id, shoppinglist_id, householdproduct_id, checked, status',
        cachedProducts: 'id, household_id, display_name, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [householdproductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        // pkey stays `my_product_id` per the Dexie limitation noted above.
        purchaseStats: 'my_product_id, household_id, last_purchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        // storeItemOrder pkey is `id`; indexed columns (`my_product_id` and
        // the compound `[store_id+my_product_id]`) rebuild with the new name.
        storeItemOrder:
          'id, store_id, householdproduct_id, [store_id+householdproduct_id]',
      })
      .upgrade(async (tx) => {
        // cachedProducts row data: rename seed_myproduct_id ->
        // seed_householdproduct_id; rename retailer_preferences[].my_product_id
        // -> householdproduct_id.
        await tx
          .table('cachedProducts')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('seed_myproduct_id' in row) {
              row.seed_householdproduct_id = row.seed_myproduct_id;
              delete row.seed_myproduct_id;
            }
            if (Array.isArray(row.retailer_preferences)) {
              row.retailer_preferences = (
                row.retailer_preferences as Array<Record<string, unknown>>
              ).map((pref) => {
                if ('my_product_id' in pref) {
                  pref.householdproduct_id = pref.my_product_id;
                  delete pref.my_product_id;
                }
                return pref;
              });
            }
          });

        // cachedListItems: rename shopping_list_id -> shoppinglist_id;
        // my_product_id -> householdproduct_id (non-pkey indexed columns).
        await tx
          .table('cachedListItems')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('shopping_list_id' in row) {
              row.shoppinglist_id = row.shopping_list_id;
              delete row.shopping_list_id;
            }
            if ('my_product_id' in row) {
              row.householdproduct_id = row.my_product_id;
              delete row.my_product_id;
            }
          });

        // storeItemOrder: rename my_product_id -> householdproduct_id on row
        // payloads; the compound index rebuilds on declaration above.
        await tx
          .table('storeItemOrder')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('my_product_id' in row) {
              row.householdproduct_id = row.my_product_id;
              delete row.my_product_id;
            }
          });
      });

    // v11 — T12.0.18.1.1 D2 within-chunk column-cleanup rename. The Postgres
    // sweep collapses within-chunk underscores on every non-exempt column
    // (standard suffixes `_id` / `_at` / `_count` / `_pct` / `_url` / `_uuid`
    // / `_by` and the standard `is_` prefix stay) and applies the seven
    // named semantic renames. The cached payload mirrors the Postgres
    // rename for every cached table whose row shape mirrors a renamed
    // Postgres column.
    //
    // Indexed-column renames in .stores() (rebuild under the new name):
    //   - cachedProducts.display_name        -> displayname
    //   - purchaseStats.last_purchased_at    -> lastpurchased_at
    //
    // Payload-key rewrites (non-indexed):
    //   - cachedProducts        (mirrors `householdproducts`)
    //   - cachedListItems       (mirrors `shoppinglistitems`)
    //   - cachedSuggestions     (mirrors `v_product_suggestions` view; includes
    //                            the `store` -> `storelocation` cascade from
    //                            T12.0.18.1 that the v_product_suggestions
    //                            output picks up in this Task)
    //
    // Out of scope at this layer:
    //   - storeItemOrder        owned by sibling T12.0.18.2 (restructure)
    //   - purchaseStats payload owned by sibling T12.0.18.3 (table cleanup);
    //                           only the indexed-column rename lands here
    //   - shoppingLists         audit reports no renames on `shoppinglists`
    //   - pendingEventsV2 / syncStateV2  camelCase JS payload, not Postgres
    //                           column mirrors; Boundaries list as legacy
    //                           carry-over.
    //
    // Dexie pkey constraint reminder: `purchaseStats` and `cachedSuggestions`
    // keep `my_product_id` as their pkey at v11 (Dexie can't change a pkey on
    // an existing version). Sync layer maps Postgres `householdproduct_id` to
    // the legacy Dexie pkey name. Resolved at v15 (T12.0.18.5): drop +
    // recreate under `purchaseCadences` / `productSuggestions` with a
    // surrogate UUID `id` pkey.
    this.version(11)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems:
          'id, shoppinglist_id, householdproduct_id, checked, status',
        // display_name -> displayname (indexed-column rename, within-chunk
        // collapse). Other indexed columns single-chunk + standard suffix;
        // unchanged.
        cachedProducts: 'id, household_id, displayname, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [householdproductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        // pkey stays `my_product_id` per Dexie limitation. Indexed
        // last_purchased_at -> lastpurchased_at (within-chunk collapse around
        // the standard `_at` suffix).
        purchaseStats: 'my_product_id, household_id, lastpurchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        // Owned by sibling T12.0.18.2 — unchanged here.
        storeItemOrder:
          'id, store_id, householdproduct_id, [store_id+householdproduct_id]',
      })
      .upgrade(async (tx) => {
        // cachedProducts mirrors `householdproducts`. Every snake_case
        // payload key listed in the Step 1 audit collapses to its
        // within-chunk form. `_url`, `_id`, `_at`, `_by` standard suffixes
        // retained; `image_url`, `seed_householdproduct_id`, `seeded_at`,
        // `created_by`, `created_at`, `updated_at`, `household_id`,
        // `category_id`, `notes`, `tags` stay as-is.
        await tx
          .table('cachedProducts')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('display_name' in row) {
              row.displayname = row.display_name;
              delete row.display_name;
            }
            if ('preferred_unit_type' in row) {
              row.preferredunittype = row.preferred_unit_type;
              delete row.preferred_unit_type;
            }
            if ('preferred_package_size_value' in row) {
              row.preferredpackagesizevalue = row.preferred_package_size_value;
              delete row.preferred_package_size_value;
            }
            if ('preferred_package_size_unit' in row) {
              row.preferredpackagesizeunit = row.preferred_package_size_unit;
              delete row.preferred_package_size_unit;
            }
            if ('default_quantity' in row) {
              row.defaultquantity = row.default_quantity;
              delete row.default_quantity;
            }
            if ('allow_substitutions' in row) {
              row.allowsubstitutions = row.allow_substitutions;
              delete row.allow_substitutions;
            }
            if ('substitution_notes' in row) {
              row.substitutionnotes = row.substitution_notes;
              delete row.substitution_notes;
            }
            if ('always_keep_stocked' in row) {
              row.alwayskeepstocked = row.always_keep_stocked;
              delete row.always_keep_stocked;
            }
            if ('expected_price_value' in row) {
              row.expectedpricevalue = row.expected_price_value;
              delete row.expected_price_value;
            }
            if ('expected_price_currency' in row) {
              row.expectedpricecurrency = row.expected_price_currency;
              delete row.expected_price_currency;
            }
            if ('expected_price_notes' in row) {
              row.expectedpricenotes = row.expected_price_notes;
              delete row.expected_price_notes;
            }
          });

        // cachedListItems mirrors `shoppinglistitems`. Audit:
        //   - selected_retaileroffer_id -> selectedretaileroffer_id
        //   - quantity_unit -> quantityunit
        // `shoppinglist_id` and `householdproduct_id` are already in their
        // v10 form (cascade rename in T12.0.18.1). `_by` and `_at` suffixes
        // retained on `added_by`, `purchased_by`, `purchased_at`,
        // `created_at`, `updated_at`.
        await tx
          .table('cachedListItems')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('selected_retaileroffer_id' in row) {
              row.selectedretaileroffer_id = row.selected_retaileroffer_id;
              delete row.selected_retaileroffer_id;
            }
            if ('quantity_unit' in row) {
              row.quantityunit = row.quantity_unit;
              delete row.quantity_unit;
            }
          });

        // cachedSuggestions mirrors `v_product_suggestions` view. The view's
        // SELECT-as column labels rename per Step 1 audit lines 280-298,
        // including the `store` -> `storelocation` cascade that originated in
        // T12.0.18.1. Pkey `my_product_id` + indexed `household_id` / `score`
        // stay.
        await tx
          .table('cachedSuggestions')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('product_name' in row) {
              row.productname = row.product_name;
              delete row.product_name;
            }
            if ('category_name' in row) {
              row.categoryname = row.category_name;
              delete row.category_name;
            }
            if ('suggested_quantity' in row) {
              row.suggestedquantity = row.suggested_quantity;
              delete row.suggested_quantity;
            }
            if ('suggested_unit' in row) {
              row.suggestedunit = row.suggested_unit;
              delete row.suggested_unit;
            }
            // store -> storelocation cascade (from T12.0.18.1) folded in with
            // the within-chunk collapse.
            if ('suggested_store_id' in row) {
              row.suggestedstorelocation_id = row.suggested_store_id;
              delete row.suggested_store_id;
            }
            if ('suggested_store_name' in row) {
              row.suggestedstorelocationname = row.suggested_store_name;
              delete row.suggested_store_name;
            }
            if ('days_since_last_purchase' in row) {
              row.dayssincelastpurchase = row.days_since_last_purchase;
              delete row.days_since_last_purchase;
            }
            if ('days_overdue' in row) {
              row.daysoverdue = row.days_overdue;
              delete row.days_overdue;
            }
          });

        // purchaseStats: indexed-column rename only. The non-indexed payload
        // (most_frequent_store_*, total_purchases, monthly_purchase_counts,
        // etc.) is owned by sibling T12.0.18.3 (product_purchase_stats
        // restructure) and NOT rewritten here. The indexed
        // `last_purchased_at` column rebuilds under `lastpurchased_at` per
        // the `.stores()` declaration; we still walk the rows so any
        // payload-level legacy key is moved onto the new name (Dexie
        // rebuilds the index against the new payload key).
        await tx
          .table('purchaseStats')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('last_purchased_at' in row) {
              row.lastpurchased_at = row.last_purchased_at;
              delete row.last_purchased_at;
            }
          });
      });

    // v12 — T12.0.18.1.1 Step 3 follow-up (2026-05-27). Reverts v11's
    // within-chunk payload-key collapse so the cache mirrors the backend wire
    // shape, which stayed on snake_case-with-new-term. The architect's call:
    // align the client on the server's emitted shape (precedent already set
    // by `RetailerOffer.sale_description`) rather than diverge.
    //
    // Indexed-column renames in .stores() (rebuild under the new name):
    //   - cachedProducts.displayname        -> display_name
    //   - purchaseStats.lastpurchased_at    -> last_purchased_at
    //
    // Payload-key rewrites (non-indexed) — exact reverse of v11:
    //   - cachedProducts        (mirrors `householdproducts`)
    //   - cachedListItems       (mirrors `shoppinglistitems`)
    //   - cachedSuggestions     (mirrors `v_product_suggestions`)
    //   - purchaseStats         (indexed column only — non-indexed payload
    //                            still owned by sibling T12.0.18.3)
    //
    // The Dexie pkey constraint from v10/v11 stands: `purchaseStats` and
    // `cachedSuggestions` keep `my_product_id` as their pkey.
    this.version(12)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems:
          'id, shoppinglist_id, householdproduct_id, checked, status',
        // displayname -> display_name (indexed-column revert).
        cachedProducts: 'id, household_id, display_name, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [householdproductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        // pkey stays `my_product_id` per Dexie limitation. Indexed
        // lastpurchased_at -> last_purchased_at (revert of v11 collapse).
        purchaseStats: 'my_product_id, household_id, last_purchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        // Owned by sibling T12.0.18.2 — unchanged here.
        storeItemOrder:
          'id, store_id, householdproduct_id, [store_id+householdproduct_id]',
      })
      .upgrade(async (tx) => {
        // cachedProducts: reverse v11's within-chunk collapse on every key it
        // touched. Keys absent from a given row are skipped (the original v11
        // upgrade only rewrote keys that were present).
        await tx
          .table('cachedProducts')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('displayname' in row) {
              row.display_name = row.displayname;
              delete row.displayname;
            }
            if ('preferredunittype' in row) {
              row.preferred_unit_type = row.preferredunittype;
              delete row.preferredunittype;
            }
            if ('preferredpackagesizevalue' in row) {
              row.preferred_package_size_value = row.preferredpackagesizevalue;
              delete row.preferredpackagesizevalue;
            }
            if ('preferredpackagesizeunit' in row) {
              row.preferred_package_size_unit = row.preferredpackagesizeunit;
              delete row.preferredpackagesizeunit;
            }
            if ('defaultquantity' in row) {
              row.default_quantity = row.defaultquantity;
              delete row.defaultquantity;
            }
            if ('allowsubstitutions' in row) {
              row.allow_substitutions = row.allowsubstitutions;
              delete row.allowsubstitutions;
            }
            if ('substitutionnotes' in row) {
              row.substitution_notes = row.substitutionnotes;
              delete row.substitutionnotes;
            }
            if ('alwayskeepstocked' in row) {
              row.always_keep_stocked = row.alwayskeepstocked;
              delete row.alwayskeepstocked;
            }
            if ('expectedpricevalue' in row) {
              row.expected_price_value = row.expectedpricevalue;
              delete row.expectedpricevalue;
            }
            if ('expectedpricecurrency' in row) {
              row.expected_price_currency = row.expectedpricecurrency;
              delete row.expectedpricecurrency;
            }
            if ('expectedpricenotes' in row) {
              row.expected_price_notes = row.expectedpricenotes;
              delete row.expectedpricenotes;
            }
          });

        // cachedListItems: reverse selectedretaileroffer_id and quantityunit.
        await tx
          .table('cachedListItems')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('selectedretaileroffer_id' in row) {
              row.selected_retaileroffer_id = row.selectedretaileroffer_id;
              delete row.selectedretaileroffer_id;
            }
            if ('quantityunit' in row) {
              row.quantity_unit = row.quantityunit;
              delete row.quantityunit;
            }
          });

        // cachedSuggestions: reverse every v_product_suggestions payload-key
        // collapse v11 applied. The suggested_storelocation_* pair keeps the
        // `store -> storelocation` cascade (that was an independent rename in
        // T12.0.18.1, not part of the within-chunk collapse).
        await tx
          .table('cachedSuggestions')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('productname' in row) {
              row.product_name = row.productname;
              delete row.productname;
            }
            if ('categoryname' in row) {
              row.category_name = row.categoryname;
              delete row.categoryname;
            }
            if ('suggestedquantity' in row) {
              row.suggested_quantity = row.suggestedquantity;
              delete row.suggestedquantity;
            }
            if ('suggestedunit' in row) {
              row.suggested_unit = row.suggestedunit;
              delete row.suggestedunit;
            }
            if ('suggestedstorelocation_id' in row) {
              row.suggested_storelocation_id = row.suggestedstorelocation_id;
              delete row.suggestedstorelocation_id;
            }
            if ('suggestedstorelocationname' in row) {
              row.suggested_storelocation_name = row.suggestedstorelocationname;
              delete row.suggestedstorelocationname;
            }
            if ('dayssincelastpurchase' in row) {
              row.days_since_last_purchase = row.dayssincelastpurchase;
              delete row.dayssincelastpurchase;
            }
            if ('daysoverdue' in row) {
              row.days_overdue = row.daysoverdue;
              delete row.daysoverdue;
            }
          });

        // purchaseStats: revert the indexed-column collapse. Non-indexed
        // payload is still owned by sibling T12.0.18.3.
        await tx
          .table('purchaseStats')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('lastpurchased_at' in row) {
              row.last_purchased_at = row.lastpurchased_at;
              delete row.lastpurchased_at;
            }
          });
      });

    // v13 — T12.0.18.2 (2026-05-27). The legacy `storeItemOrder` Dexie store
    // (3-way household-scoped junction keyed on store + householdproduct) is
    // replaced by `aislePositions` — a 2-way (storelocation, retaileroffer)
    // junction. Aisle position is a physical store property, shared across
    // every household that shops there; the row carries no household_id.
    //
    // Backend FK direction swaps from HouseholdProduct to RetailerOffer
    // (catalog grain). The household-scoped legacy shape is dropped — there
    // is no row-level migration that makes sense (the legacy row referenced a
    // household-private HouseholdProduct; the new row references a shared
    // RetailerOffer). Existing local rows on `storeItemOrder` are dropped
    // with the store; no upgrade callback rewrites them. Server-side data is
    // already empty in dev.
    //
    // The Dexie pkey stays a surrogate UUID `id` (consistent with the legacy
    // store_item_order and the T12.0.18.5 surrogate-pkey pattern); the
    // composite uniqueness lives server-side.
    this.version(13)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems:
          'id, shoppinglist_id, householdproduct_id, checked, status',
        cachedProducts: 'id, household_id, display_name, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [householdproductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        purchaseStats: 'my_product_id, household_id, last_purchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        // T12.0.18.2 — drop the legacy household-scoped store.
        storeItemOrder: null,
        // T12.0.18.2 — shared per-StoreLocation aisle positions. Surrogate
        // UUID `id` is the Dexie pkey; uniqueness on
        // (storelocation_id, retaileroffer_id) lives server-side.
        aislePositions:
          'id, storelocation_id, retaileroffer_id, [storelocation_id+retaileroffer_id]',
      });

    // v14 — T12.0.18.3 (2026-05-27). The Postgres `product_purchase_stats`
    // table is renamed to `purchasecadences` (PurchaseCadence entity), and
    // every non-standard-suffix column is collapsed per the D2 within-chunk
    // rule. Two named semantic renames also land here:
    //   - `stats_computed_at`        -> `computed_at`        (D1 jargon-strip;
    //                                                         table name
    //                                                         carries the noun)
    //   - `predicted_next_purchase`  -> `predictednextpurchase_at` (add `_at`
    //                                                         standard suffix
    //                                                         on a timestamp
    //                                                         column)
    //
    // Indexed-column rename in .stores() (rebuild under the new name):
    //   - purchaseStats.last_purchased_at -> lastpurchased_at
    //
    // Payload-key rewrites (non-indexed) on `purchaseStats`:
    //   - total_purchases                -> totalpurchases
    //   - first_purchased_at             -> firstpurchased_at
    //   - avg_days_between_purchases     -> avgdaysbetweenpurchases
    //   - purchase_interval_stddev       -> purchaseintervalstddev
    //   - avg_quantity                   -> avgquantity
    //   - most_common_quantity           -> mostcommonquantity
    //   - most_common_unit               -> mostcommonunit
    //   - store_purchase_counts          -> storelocationpurchasecounts
    //                                       (D7 rule 5 cascade)
    //   - most_frequent_store_id         -> mostfrequentstorelocation_id
    //                                       (D7 rule 5 cascade)
    //   - most_frequent_store_name       -> mostfrequentstorelocationname
    //                                       (D7 rule 5 cascade)
    //   - monthly_purchase_counts        -> monthlypurchasecounts
    //   - peak_month                     -> peakmonth
    //   - avg_price_paid                 -> avgpricepaid
    //   - last_price_paid                -> lastpricepaid
    //   - predicted_next_purchase        -> predictednextpurchase_at
    //   - prediction_confidence          -> predictionconfidence
    //   - stats_computed_at              -> computed_at
    //
    // Dexie pkey constraint: `purchaseStats` keeps `my_product_id` as its
    // primary key at v14 per Dexie's no-pkey-change-on-upgrade rule. The
    // sync layer maps Postgres `householdproduct_id` to the legacy Dexie
    // pkey name. Resolved at v15 (T12.0.18.5): drop + recreate under
    // `purchaseCadences` on a surrogate UUID `id` pkey with indexed
    // `householdproduct_id`.
    this.version(14)
      .stores({
        items: 'id, household_id, name, is_needed, assigned_store_id, status',
        stores: 'id, household_id, name',
        pendingEvents: 'id, itemId, synced, clientTimestamp',
        syncState: 'deviceId',

        cachedListItems:
          'id, shoppinglist_id, householdproduct_id, checked, status',
        cachedProducts: 'id, household_id, display_name, category_id',
        shoppingLists: 'id, household_id, status, list_type',
        pendingEventsV2:
          'id, [shoppingListItemId+synced], [householdproductId+synced], synced, clientTimestamp',
        syncStateV2: 'deviceId',

        // pkey stays `my_product_id` per Dexie limitation. Indexed
        // last_purchased_at -> lastpurchased_at (within-chunk collapse around
        // the standard `_at` suffix).
        purchaseStats: 'my_product_id, household_id, lastpurchased_at',
        cachedSuggestions: 'my_product_id, household_id, score',

        aislePositions:
          'id, storelocation_id, retaileroffer_id, [storelocation_id+retaileroffer_id]',
      })
      .upgrade(async (tx) => {
        await tx
          .table('purchaseStats')
          .toCollection()
          .modify((row: Record<string, unknown>) => {
            if ('total_purchases' in row) {
              row.totalpurchases = row.total_purchases;
              delete row.total_purchases;
            }
            if ('first_purchased_at' in row) {
              row.firstpurchased_at = row.first_purchased_at;
              delete row.first_purchased_at;
            }
            if ('last_purchased_at' in row) {
              row.lastpurchased_at = row.last_purchased_at;
              delete row.last_purchased_at;
            }
            if ('avg_days_between_purchases' in row) {
              row.avgdaysbetweenpurchases = row.avg_days_between_purchases;
              delete row.avg_days_between_purchases;
            }
            if ('purchase_interval_stddev' in row) {
              row.purchaseintervalstddev = row.purchase_interval_stddev;
              delete row.purchase_interval_stddev;
            }
            if ('avg_quantity' in row) {
              row.avgquantity = row.avg_quantity;
              delete row.avg_quantity;
            }
            if ('most_common_quantity' in row) {
              row.mostcommonquantity = row.most_common_quantity;
              delete row.most_common_quantity;
            }
            if ('most_common_unit' in row) {
              row.mostcommonunit = row.most_common_unit;
              delete row.most_common_unit;
            }
            if ('store_purchase_counts' in row) {
              row.storelocationpurchasecounts = row.store_purchase_counts;
              delete row.store_purchase_counts;
            }
            if ('most_frequent_store_id' in row) {
              row.mostfrequentstorelocation_id = row.most_frequent_store_id;
              delete row.most_frequent_store_id;
            }
            if ('most_frequent_store_name' in row) {
              row.mostfrequentstorelocationname = row.most_frequent_store_name;
              delete row.most_frequent_store_name;
            }
            if ('monthly_purchase_counts' in row) {
              row.monthlypurchasecounts = row.monthly_purchase_counts;
              delete row.monthly_purchase_counts;
            }
            if ('peak_month' in row) {
              row.peakmonth = row.peak_month;
              delete row.peak_month;
            }
            if ('avg_price_paid' in row) {
              row.avgpricepaid = row.avg_price_paid;
              delete row.avg_price_paid;
            }
            if ('last_price_paid' in row) {
              row.lastpricepaid = row.last_price_paid;
              delete row.last_price_paid;
            }
            if ('predicted_next_purchase' in row) {
              row.predictednextpurchase_at = row.predicted_next_purchase;
              delete row.predicted_next_purchase;
            }
            if ('prediction_confidence' in row) {
              row.predictionconfidence = row.prediction_confidence;
              delete row.prediction_confidence;
            }
            if ('stats_computed_at' in row) {
              row.computed_at = row.stats_computed_at;
              delete row.stats_computed_at;
            }
          });
      });

    // v15 — T12.0.18.5 (2026-05-28). Drop the legacy `purchaseStats` and
    // `cachedSuggestions` stores; create fresh `purchaseCadences` and
    // `productSuggestions` stores on a surrogate UUID `id` pkey with indexed
    // `householdproduct_id`. The drop + recreate carries no `.upgrade()`
    // callback — cached rows on existing dev installs are dropped and
    // re-populated from the server on next sync. No production users.
    //
    // The two new store property names align with their entities
    // (PurchaseCadence, ProductSuggestion); the legacy `purchaseStats` name
    // dated to v4 (pre-PurchaseCadence) and the `cachedSuggestions` name
    // carried the storage-tier `cached*` prefix. Backend's
    // `productSuggestionsService` + `v_product_suggestions` source align
    // with the new name.
    this.version(15).stores({
      purchaseStats: null,
      cachedSuggestions: null,
      purchaseCadences: 'id, householdproduct_id, household_id, lastpurchased_at',
      productSuggestions: 'id, householdproduct_id, household_id, score',
    });
  }
}
